/* global optional_vitamins, aggregate_vitamins */

import jQuery from "jquery";

import { currentFormat, isFormat, isLegacyFDA, isFDA, isCanadian, isAggregate } from "./format.js";
import { tabularShuffle } from "./layout.js";
import { rebuildFunction, getTotalVitamins } from "./vitamin_toggles.js";
import { refreshSlider } from "./sliders.js";
import { adjustNewLinearLineHeightForCalorieFont, showFootnoteSeparator } from "./layout.js";
import { saveLabel } from "./persistence.js";
import { syntheticEvent } from "./util.js";
import { setSimplified, formatCanSimplify } from "./simplified.js";
import { onFormat } from "./format.js";
import { saveProteinScore } from "./persistence.js";
import { displayError } from "jose_modern/field_with_errors.js";
import { simplifiedCheckboxForFormat } from "./simplified.js";
import { toggleOverrideInput } from "./overrides.js";
import { once } from "jose_modern/once.js";

const $ = jQuery;

export const SELECTOR_FOR_NUTRIENT_WITH_VISIBILITY_TOGGLE = {
  "fda/adult/vertical": {
    fat_saturated: "#new_vertical_saturated_fat",
    fat_trans: "#new_vertical_trans_fat",
    cholesterol: "#new_vertical_cholesterol",
    new_fiber: "#new_vertical_fiber",
    sugar: "#new_vertical_sugar",
    added_sugar: "#new_vertical_added_sugar",
  },
  "fda/adult/linear": {
    fat_saturated: "#satfatnewlinear",
    fat_trans: "#transfatnewlinear",
    cholesterol: "#cholesterolnewlinear",
    new_fiber: "#fibernewlinear",
    sugar: "#sugarsnewlinear",
    added_sugar: "#addedsugarnewlinear",
  },
  "fda/adult/tabular": {
    fat_saturated: "#satfatnewtabular",
    fat_trans: "#transfatnewtabular",
    cholesterol: "#cholesterolnewtabular",
    new_fiber: "#fibernewtabular",
    sugar: "#sugarnewtabular",
    added_sugar: "#addedsugarnewtabular",
  },
  "fda/adult/vertical/as_prepared": {
    fat_saturated: "#satfatnewaspackaged",
    fat_trans: "#transfatnewaspackaged",
    cholesterol: "#cholesterolnewaspackaged",
    new_fiber: "#fibernewaspackaged",
    sugar: "#sugarnewaspackaged",
    added_sugar: "#addedsugarnewaspackaged",
  },
  "fda/adult/vertical/aggregate": {
    fat_saturated: "#satfatnewaggregate",
    fat_trans: "#transfatnewaggregate",
    cholesterol: "#cholesterolnewaggregate",
    new_fiber: "#fibernewaggregate",
    sugar: "#sugarnewaggregate",
    added_sugar: "#addedsugarnewaggregate",
  },
};

const NUTRIENT_TOGGLE_CALLBACK = {
  "fda/adult/linear": adjustNewLinearLineHeightForCalorieFont,
  "fda/adult/tabular": tabularShuffle,
};

const DEPENDENT_NUTRIENTS = {
  added_sugar: [{ "##checked": ["sugar"] }],
  sugar: [{ "##unchecked": ["added_sugar"] }],
};

function inSimplifiedMode() {
  return !!simplifiedCheckboxForFormat()?.is(":checked");
}

export function formatSupportsVisibilityToggle() {
  return (
    isFormat("fda/adult/vertical") ||
    isFormat("fda/adult/linear") ||
    isFormat("fda/adult/tabular") ||
    isFormat("fda/adult/vertical/as_prepared") ||
    isFormat("fda/adult/vertical/aggregate")
  );
}

export function bindOptionalNutrientPanel() {
  bindSimplifiedToggle();
  bindInsignificantToggle();
  bindSatFatCalories();
  bindPolyMonoUnsaturatedFatsToggle();
  bindOmegaToggles();
  bindPotassiumToggle();
  bindOtherCarbohydrateToggle();
  bindSugarAlcoholToggle();
  bindCholesterolDvToggle();
  bindAddedSugar();
  bindProteinPecent();
  bindNutrientVisibilityToggles();
}

export function initOptionalNutrientPanel(label) {
  resetOptionalNutrientView();
  showOptionalNutrientToggles();

  const simplified =
    label.simplified && formatCanSimplify() && simplifiedCheckboxForFormat()?.length > 0;
  const anyChecked = syncVisibilityToggleCheckboxes(label, simplified);

  setSimplified(label.simplified);

  syncInsignificantVisibility(simplified, anyChecked);

  applySetting("#satfatcaloriestoggle", label.show_sat_fat_calories, toggleSatFatCalories);
  applySetting("#polymonounsatfattoggle", label.show_unsat_fat, togglePolyMonoUnsaturatedFats);
  applySetting("#omegastoggle", label.show_omegas, toggleOmegas);
  applySetting("#potassiumtoggle", label.show_potassium, togglePotassium);
  applySetting("#othercarbtoggle", label.show_other_carbs, toggleOtherCarbohyrate);
  applySetting("#hideaddedsugartoggle", label.show_added_sugar_grams, toggleAddedSugar, true);
  applySetting("#sugaralcoholtoggle", label.show_sugar_alcohol, toggleSugarAlcohol);
  applySetting("#cholesteroldvtoggle", label.show_cholesterol_dv, toggleCholesterolDv);
  applySetting("#protein-percent-toggle", label.show_protein_percent, toggleProteinPercent);

  syncInsignificantWithSelectedNutrients({ save: false });

  checkAndShowCanadianSugarAlcohol();
}

function applySetting(selector, enabled, updateFunction, toggleInverted = false) {
  // The rendered UI is still the arbiter of what options are available (set via
  // the formatters), regardless of the toggle value set in the recipe label.
  // Therefore, we need to check to see if the toggle exists in the dom before
  // we update the rendered state.
  const toggle = document.querySelector(selector);
  if (toggle) {
    toggle.checked = toggleInverted ? !enabled : enabled;
    updateFunction(enabled);
  } else {
    // When not present, act as if the checkbox is in its default state
    const defaultState = toggleInverted;
    updateFunction(defaultState);
  }
}

export function checkAndShowCanadianSugarAlcohol() {
  // mandatory sugar alcohol for canada if present
  if (
    isFormat("canada/adult/vertical") ||
    isFormat("canada/adult/tabular") ||
    isFormat("canada/adult/vertical/as_prepared") ||
    isFormat("canada/adult/linear")
  ) {
    if (optional_vitamins["sugar_alcohol"] > 0) {
      $("#sugaralcoholtoggle").prop("checked", true);
      toggleSugarAlcohol(true);
    }
  } else if (isFormat("canada/adult/vertical/aggregate")) {
    if (aggregate_vitamins.some((vitamins) => vitamins["sugar_alcohol"] > 0)) {
      $("#sugaralcoholtoggle").prop("checked", true);
      toggleSugarAlcohol(true);
    }
  }
}

function resetOptionalNutrientView() {
  $(".protein-percent.optional, .protein-percent-conversion.optional").hide();
  document.querySelector("#protein-score-container").classList.add("is-hidden");
  $(
    "#potassium, #potassiumhairline, #potassiumpercentconversion, #potassiumpercentconversiontabular, #potassium-aggregate, #potassiumpercentconversionaggregate"
  ).hide();
  $("#linearpotassium, #potassiumtabular").hide();
  $(
    "#satfatcalories, #satfatcaloriescanada, #linearsatfatcalories, #satfatcaloriestabular, #satfatcalories-aggregate"
  ).hide();
  $(
    "#polyunsatfat, #polyunsatfattabular, #polyunsatfatnewtabular, #polyunsatfat-new-as-prepared, #polyunsatfat-new-aggregate, .new-canada-polyunsaturated, #polyunsaturatednewlinearcanada"
  ).hide();
  $(
    "#monounsatfat, #monounsatfattabular, #monounsatfatnewtabular, #monounsatfat-new-as-prepared, #monounsatfat-new-aggregate, .new-canada-monounsaturated, #monounsaturatednewlinearcanada"
  ).hide();
  $("#linearunsatfat, #newlinearunsatfat").hide();
  $("#othercarb, #linearothercarb, #othercarbtabular, #othercarb-aggregate").hide();
  $(".added-sugar-grams").show();
  $(
    "#sugaralcohol, #sugaralcohollinear, #sugaralcoholtabular, #sugaralcoholaggregate, #sugaralcoholnewvertical, #sugaralcoholnewtabular, #sugaralcoholnewlinear, #sugaralcoholnewasprepared, #sugaralcoholnewaggregate, .new-canada-sugar-alcohol, #sugaralcoholnewlinearcanada"
  ).hide();
  $(
    "#insignificant-nutrients-list, #insignificant-nutrients-list-tabular, #insignificant-nutrients-list-new-tabular, #insignificant-nutrients-list-new-linear, #insignificant-nutrients-list-linear, #insignificant-nutrients-list-as-packaged, #insignificant-nutrients-list-new-as-packaged, #insignificant-nutrients-list-new-aggregate"
  ).hide();
}

export function showOptionalNutrientToggles() {
  if (isFDA() || isLegacyFDA()) {
    jQuery("#protein-percent-toggle-container, #protein-score-container").show();
  } else {
    jQuery("#protein-percent-toggle-container, #protein-score-container").hide();
  }

  if (
    isFormat("fda/adult/vertical") ||
    isFormat("fda/adult/tabular") ||
    isFormat("fda/adult/linear") ||
    isFormat("fda/adult/vertical/as_prepared") ||
    isFormat("fda/adult/vertical/aggregate")
  ) {
    jQuery(".satfatcaloriestoggle").hide();
    jQuery(".potassiumtoggle").hide();
    jQuery(".othercarbtoggle").hide();
    jQuery(".simplifiedformattogglesection, .newcanadasimplifiedformattogglesection").hide();

    if (isFormat("fda/adult/vertical/aggregate")) {
      // Aggregates have a separate set of simplified/footnote toggles, so we
      // need to hide the default ones and toggle simplified
      jQuery(".newsimplifiedformattogglesection").toggle(false);
      jQuery(".newaggregatesimplifiedformattogglesection.simplifiedformattoggle").toggle(true);
    } else {
      jQuery(".newsimplifiedformattogglesection.simplifiedformattoggle").toggle(true);
      jQuery(".newaggregatesimplifiedformattogglesection").toggle(false);
    }
  } else if (
    isFormat("canada/adult/vertical") ||
    isFormat("canada/adult/linear") ||
    isFormat("canada/adult/tabular") ||
    isFormat("canada/adult/vertical/aggregate")
  ) {
    jQuery(".satfatcaloriestoggle").hide();
    jQuery(".potassiumtoggle").hide();
    jQuery(".othercarbtoggle").hide();
    jQuery(
      ".simplifiedformattogglesection, .newsimplifiedformattogglesection, .newaggregatesimplifiedformattogglesection"
    ).hide();
    jQuery(".newcanadasimplifiedformattogglesection").show();
  } else if (isFormat("canada/adult/vertical/as_prepared")) {
    // Same as other canada formats, but doesn't have a simplified format
    jQuery(".satfatcaloriestoggle").hide();
    jQuery(".potassiumtoggle").hide();
    jQuery(".othercarbtoggle").hide();

    jQuery(
      ".newcanadasimplifiedformattogglesection, .simplifiedformattogglesection, .newsimplifiedformattogglesection, .newaggregatesimplifiedformattogglesection"
    ).hide();
  } else {
    jQuery(".satfatcaloriestoggle").show();
    jQuery(".potassiumtoggle").show();
    jQuery(".simplifiedformattogglesection").show();
    jQuery(
      ".newsimplifiedformattogglesection, .newcanadasimplifiedformattogglesection, .newaggregatesimplifiedformattogglesection"
    ).hide();
    jQuery(".othercarbtoggle").show();
  }
}

function bindSimplifiedToggle() {
  //Simplified label format
  $(
    "#simplifiedformattoggle, #newsimplifiedformattoggle, #newaggregatesimplifiedformattoggle, #newcanadasimplifiedformattoggle"
  ).on("change", (ev) => {
    const alsoShortenFootnote = !syntheticEvent(ev);
    const simplified = $(ev.target).is(":checked");

    const anyChecked = syncVisibilityToggleCheckboxes(window.recipe_label, simplified);
    setSimplified(simplified, { updateControls: alsoShortenFootnote });
    syncInsignificantVisibility(simplified, anyChecked);

    saveLabel({
      simplified,
      shorten_footnote: $("#asterisksectionminitoggle").is(":checked"),
    });
  });
}

function bindInsignificantToggle() {
  // show state of insignificant nutrients not displayed on label
  $("#insignificanttoggle, #newinsignificanttoggle, #newaggregateinsignificanttoggle").on(
    "click",
    () => {
      const display = insignificantCheckboxForFormat().is(":checked");
      const simplified = inSimplifiedMode();

      // toggles.js clicks the insignificant toggle if show_insignificant is set
      // but with visibility toggles, the insignificant element cannot be
      // displayed on standard labels, even if it's empty.
      if (formatSupportsVisibilityToggle() && !simplified) return;

      toggleInsignificant(display, simplified);

      saveLabel({
        show_insignificant: display,
      });
    }
  );

  bindElementsThatImpactInsignificant(
    "#optionalnutrientsdiv input:checkbox:not(.nutrientvisibilitytoggle)"
  );
}

const selectedOptionalNutrients =
  "#optionalvitaminsdiv :checkbox:checked, #optionalnutrientsdiv .optionalnutrientvalue :checkbox:checked:not(#hideaddedsugartoggle):not(.nutrientvisibilitytoggle)";

function syncInsignificantWithSelectedNutrients({ save = true } = {}) {
  const insignificantCheckbox = insignificantCheckboxForFormat();
  if (!insignificantCheckbox) return;

  const isSimplified = simplifiedCheckboxForFormat().is(":checked");

  // In standard mode the insignificant footnote is driven by nutrient
  // visibility, not by which optional nutrients are displayed
  if (formatSupportsVisibilityToggle() && !isSimplified) return;

  const selectedCount = $(selectedOptionalNutrients).length;

  if (isSimplified && selectedCount > 0) {
    insignificantCheckbox.prop("checked", true);
  } else if (!isSimplified) {
    insignificantCheckbox.prop("checked", false);
  }

  const show_insignificant = insignificantCheckbox.is(":checked");
  toggleInsignificant(show_insignificant, isSimplified);
  if (save) {
    saveLabel({ show_insignificant });
  }
}

export function bindElementsThatImpactInsignificant(selector) {
  $(selector).on("click", () => syncInsignificantWithSelectedNutrients());
}

export const insignificantCheckboxForFormat = () => {
  if (isLegacyFDA()) {
    return $("#insignificanttoggle");
  } else if (isFDA()) {
    return isAggregate() ? $("#newaggregateinsignificanttoggle") : $("#newinsignificanttoggle");
  } else {
    return null;
  }
};

export function toggleInsignificant(display, simplified = inSimplifiedMode()) {
  if (isFormat("legacy_fda/adult/vertical")) {
    if (display) {
      $("#insignificant-nutrients-list").fadeIn("slow");
    } else {
      $("#insignificant-nutrients-list").fadeOut("slow");
    }
  } else if (isFormat("fda/adult/vertical")) {
    const footnote = $("#new-vertical-insignificant-nutrients-list");
    if (display) {
      footnote.fadeIn("slow");
    } else {
      footnote.fadeOut("slow");
    }
    footnote.toggleClass("is-standard-size", !simplified);
    footnote.promise().done(showFootnoteSeparator);
  } else if (isFormat("legacy_fda/adult/tabular")) {
    if (display) {
      $("#insignificant-nutrients-list-tabular").fadeIn("slow");
    } else {
      $("#insignificant-nutrients-list-tabular").fadeOut("slow");
    }
  } else if (isFormat("fda/adult/tabular")) {
    syncTabularHairline();

    const footnote = $("#insignificant-nutrients-list-new-tabular");
    if (display) {
      footnote.fadeIn("slow");
    } else {
      footnote.fadeOut("slow");
    }
    footnote.toggleClass("is-standard-size", !simplified);
  } else if (isFormat("legacy_fda/adult/linear")) {
    if (display) {
      $("#insignificant-nutrients-list-linear").fadeIn("slow");
    } else {
      $("#insignificant-nutrients-list-linear").fadeOut("slow");
    }
  } else if (isFormat("fda/adult/linear")) {
    if (display) {
      $("#insignificant-nutrients-list-new-linear").fadeIn("slow");
    } else {
      $("#insignificant-nutrients-list-new-linear").fadeOut("slow");
    }
  } else if (isFormat("legacy_fda/adult/vertical/as_prepared")) {
    if (display) {
      $("#insignificant-nutrients-list-as-packaged").fadeIn("slow");
    } else {
      $("#insignificant-nutrients-list-as-packaged").fadeOut("slow");
    }
  } else if (isFormat("fda/adult/vertical/as_prepared")) {
    const footnote = $("#insignificant-nutrients-list-new-as-packaged");
    if (display) {
      footnote.fadeIn("slow");
    } else {
      footnote.fadeOut("slow");
    }
    footnote.toggleClass("is-standard-size", !simplified);
    footnote.promise().done(showFootnoteSeparator);
  } else if (isFormat("fda/adult/vertical/aggregate")) {
    const footnote = $("#insignificant-nutrients-list-new-aggregate");
    if (display) {
      footnote.fadeIn("slow");
    } else {
      footnote.fadeOut("slow");
    }
    footnote.toggleClass("is-standard-size", !simplified);
  }
}

function syncTabularHairline() {
  const totalVitamins = getTotalVitamins();
  const isSingleServing = $(".newtabularsingleserving").length;
  const hasVitamins = isSingleServing ? totalVitamins > 0 : true;
  const hasFootnoteText =
    $("#insignificant-nutrients-list-new-tabular").children().text()?.trim() !== "";

  if (hasVitamins && hasFootnoteText) {
    $("#insignificant-nutrients-list-new-tabular .hairlineseparator").show();
  } else {
    $("#insignificant-nutrients-list-new-tabular .hairlineseparator").hide();
  }
}

function bindSatFatCalories() {
  //Show sat fat calories on button click
  $("#satfatcaloriestoggle").on("click", (e) => {
    toggleSatFatCalories(e.target.checked);

    saveLabel({ show_sat_fat_calories: e.target.checked });
  });
}

const toggleSatFatCalories = (display) => {
  // logic for both showing and hiding
  onFormat({
    "legacy_fda/adult/vertical"() {
      if (display) {
        $("#fatcalories").fadeOut("fast");
        $("#satfatcalories").fadeIn("slow");
        $("#satfatcaloriescanada").hide();
      } else {
        $("#satfatcalories").fadeOut("fast");
        $("#fatcalories").fadeIn("slow");
        $("#satfatcaloriescanada").hide();
      }
    },
    "legacy_fda/adult/linear"() {
      if (display) {
        $("#linearsatfatcalories").fadeIn("slow");
      } else {
        $("#linearsatfatcalories").fadeOut("slow");
      }
    },
    "legacy_fda/adult/vertical/as_prepared"() {
      if (display) {
        $("#satfatcalories-aggregate").fadeIn("slow");
      } else {
        $("#satfatcalories-aggregate").fadeOut("slow");
      }
    },
    "legacy_fda/adult/tabular"() {
      if (display) {
        $("#satfatcaloriestabular").fadeIn("slow");
      } else {
        $("#satfatcaloriestabular").fadeOut("slow");
      }
    },

    "default": () => {},
  });
};

function bindPolyMonoUnsaturatedFatsToggle() {
  //Show poly- and monounsaturated fat on button click
  $("#polymonounsatfattoggle").on("click", (e) => {
    const checked = e.target.checked;
    const saveData = { show_unsat_fat: checked };
    if (!checked) {
      saveData.show_omegas = false;

      if (isCanadian()) {
        const omegasToggle = document.querySelector("#omegastoggle");
        if (omegasToggle) omegasToggle.checked = false;

        toggleOmegas(false);
      }
    }

    togglePolyMonoUnsaturatedFats(e.target.checked);
    saveLabel(saveData);
  });
}

export function togglePolyMonoUnsaturatedFats(display) {
  toggleOverrideInput("fat_polyunsaturated", display);
  toggleOverrideInput("fat_monounsaturated", display);

  // logic for both showing and hiding
  if (isFormat("legacy_fda/adult/vertical")) {
    if (display) {
      $("#polyunsatfat").fadeIn("slow");
      $("#monounsatfat").fadeIn("slow");
    } else {
      $("#polyunsatfat").fadeOut("fast");
      $("#monounsatfat").fadeOut("fast");
    }
  } else if (isFormat("canada/adult/linear")) {
    if (display) {
      $(".polyunsaturatednewlinearcanada").fadeIn("slow");
      $(".monounsaturatednewlinearcanada").fadeIn("slow");
    } else {
      $(".polyunsaturatednewlinearcanada").fadeOut("fast");
      $(".monounsaturatednewlinearcanada").fadeOut("fast");
    }
  } else if (
    isFormat("canada/adult/vertical") ||
    isFormat("canada/adult/tabular") ||
    isFormat("canada/adult/vertical/as_prepared") ||
    isFormat("canada/adult/vertical/aggregate")
  ) {
    if (display) {
      $(".new-canada-monounsaturated").fadeIn("slow");
      $(".new-canada-polyunsaturated").fadeIn("slow");
      rebuildFunction();
    } else {
      $(".new-canada-monounsaturated").fadeOut("fast");
      $(".new-canada-polyunsaturated").fadeOut("fast", rebuildFunction);
    }
  } else if (isFormat("fda/adult/vertical")) {
    if (display) {
      $("#polyunsatfat-new-vertical, #monounsatfat-new-vertical").fadeIn("slow", () => {
        if ($("#newnutritionfactstable").length > 0) {
          // do nothing
        } else {
          refreshSlider("#newdualsliderleft");
        }
      });
    } else {
      $("#polyunsatfat-new-vertical").fadeOut("fast");
      $("#monounsatfat-new-vertical").fadeOut("fast");
    }
  } else if (isFormat("legacy_fda/adult/linear")) {
    if (display) {
      $("#linearunsatfat").fadeIn("slow");
    } else {
      $("#linearunsatfat").fadeOut("slow");
    }
  } else if (isFormat("fda/adult/linear")) {
    if (display) {
      $("#newlinearunsatfat").fadeIn("slow", () => {
        adjustNewLinearLineHeightForCalorieFont();
      });
    } else {
      $("#newlinearunsatfat").fadeOut("slow", () => {
        adjustNewLinearLineHeightForCalorieFont();
      });
    }
  } else if (isFormat("legacy_fda/adult/tabular")) {
    if (display) {
      $("#polyunsatfattabular, #monounsatfattabular").fadeIn("slow", () => {
        tabularShuffle();
      });
    } else {
      $("#polyunsatfattabular, #monounsatfattabular").fadeOut("slow", () => {
        tabularShuffle();
      });
    }
  } else if (isFormat("fda/adult/tabular")) {
    if (display) {
      $("#polyunsatfatnewtabular, #monounsatfatnewtabular").fadeIn("slow", () => {
        if ($("#new_tabular_dual_nutrient_cols").length > 0) {
          refreshSlider("#newdualtabularsliderleft");
        }
        tabularShuffle();
      });
    } else {
      $("#polyunsatfatnewtabular, #monounsatfatnewtabular").fadeOut("slow", () => {
        tabularShuffle();
      });
    }
  } else if (isFormat("legacy_fda/adult/vertical/as_prepared")) {
    if (display) {
      $("#polyunsatfat-aggregate").fadeIn("slow");
      $("#monounsatfat-aggregate").fadeIn("slow");
    } else {
      $("#polyunsatfat-aggregate").fadeOut("fast");
      $("#monounsatfat-aggregate").fadeOut("fast");
    }
  } else if (isFormat("fda/adult/vertical/as_prepared")) {
    if (display) {
      $("#polyunsatfat-new-as-prepared, #monounsatfat-new-as-prepared").fadeIn("slow", () => {
        refreshSlider("#newaspreparedsliderleft");
      });
    } else {
      $("#polyunsatfat-new-as-prepared").fadeOut("fast");
      $("#monounsatfat-new-as-prepared").fadeOut("fast");
    }
  } else if (isFormat("fda/adult/vertical/aggregate")) {
    if (display) {
      $("#polyunsatfat-new-aggregate, #monounsatfat-new-aggregate").fadeIn("slow", () => {
        refreshSlider("#newaggregatesliderleft");
      });
    } else {
      $("#polyunsatfat-new-aggregate").fadeOut("fast");
      $("#monounsatfat-new-aggregate").fadeOut("fast");
    }
  }
}

function bindOmegaToggles() {
  $("#omegastoggle").on("click", (e) => {
    const checked = e.target.checked;
    toggleOmegas(checked);
    const saveData = { show_omegas: checked };
    if (checked) {
      togglePolyMonoUnsaturatedFats(true);
      document.querySelector("#polymonounsatfattoggle").checked = true;
      saveData.show_unsat_fat = true;
    }

    if (isFormat("canada/adult/tabular")) {
      rebuildFunction();
    }

    saveLabel(saveData);
  });
}

function toggleOmegas(display) {
  toggleOverrideInput("omega_3", display);
  toggleOverrideInput("omega_6", display);

  if (display) {
    $(".canada-omega3").fadeIn("slow");
    $(".canada-omega6").fadeIn("slow");
  } else {
    $(".canada-omega3").fadeOut("fast");
    $(".canada-omega6").fadeOut("fast");
  }
}

function bindSugarAlcoholToggle() {
  //Show other carb on button click
  $("#sugaralcoholtoggle").on("click", () => {
    toggleSugarAlcohol($("#sugaralcoholtoggle").is(":checked"));

    saveLabel({ show_sugar_alcohol: $("#sugaralcoholtoggle").is(":checked") });
  });
}

export function toggleSugarAlcohol(display) {
  toggleOverrideInput("sugar_alcohol", display);

  // logic for both showing and hiding
  if (isFormat("legacy_fda/adult/vertical")) {
    if (display) {
      $("#sugaralcohol").fadeIn("slow");
    } else {
      $("#sugaralcohol").fadeOut("fast");
    }
  } else if (
    isFormat("canada/adult/vertical") ||
    isFormat("canada/adult/tabular") ||
    isFormat("canada/adult/vertical/as_prepared") ||
    isFormat("canada/adult/vertical/aggregate")
  ) {
    if (display) {
      $(".new-canada-sugar-alcohol").fadeIn("slow");
      rebuildFunction();
    } else {
      $(".new-canada-sugar-alcohol").fadeOut("fast", rebuildFunction);
    }
  } else if (isFormat("canada/adult/linear")) {
    if (display) {
      $(".sugaralcoholnewlinearcanada").fadeIn("slow");
    } else {
      $(".sugaralcoholnewlinearcanada").fadeOut("fast");
    }
  } else if (isFormat("legacy_fda/adult/linear")) {
    if (display) {
      $("#sugaralcohollinear").fadeIn("slow");
    } else {
      $("#sugaralcohollinear").fadeOut("slow");
    }
  } else if (isFormat("legacy_fda/adult/vertical/as_prepared")) {
    if (display) {
      $("#sugaralcoholaggregate").fadeIn("slow");
    } else {
      $("#sugaralcoholaggregate").fadeOut("slow");
    }
  } else if (isFormat("legacy_fda/adult/tabular")) {
    if (display) {
      $("#sugaralcoholtabular").fadeIn("slow", () => {
        tabularShuffle();
      });
    } else {
      $("#sugaralcoholtabular").fadeOut("slow", () => {
        tabularShuffle();
      });
    }
  } else if (isFormat("fda/adult/vertical")) {
    if (display) {
      $("#sugaralcoholnewvertical").fadeIn("slow");
    } else {
      $("#sugaralcoholnewvertical").fadeOut("fast");
    }
  } else if (isFormat("fda/adult/linear")) {
    if (display) {
      $("#sugaralcoholnewlinear").fadeIn("slow");
    } else {
      $("#sugaralcoholnewlinear").fadeOut("slow");
    }
  } else if (isFormat("fda/adult/vertical/as_prepared")) {
    if (display) {
      $("#sugaralcoholnewasprepared").fadeIn("slow");
    } else {
      $("#sugaralcoholnewasprepared").fadeOut("slow");
    }
  } else if (isFormat("fda/adult/vertical/aggregate")) {
    if (display) {
      $("#sugaralcoholnewaggregate").fadeIn("slow");
    } else {
      $("#sugaralcoholnewaggregate").fadeOut("slow");
    }
  } else if (isFormat("fda/adult/tabular")) {
    if (display) {
      $("#sugaralcoholnewtabular").fadeIn("slow", () => {
        tabularShuffle();
      });
    } else {
      $("#sugaralcoholnewtabular").fadeOut("slow", () => {
        tabularShuffle();
      });
    }
  }
}

function bindCholesterolDvToggle() {
  $("#cholesteroldvtoggle").on("click", (e) => {
    toggleCholesterolDv(e.target.checked);
    saveLabel({ show_cholesterol_dv: e.target.checked });
  });
}

function toggleCholesterolDv(display) {
  if (display) {
    $(".canada-cholesterol-dv").fadeIn("slow");
    rebuildFunction();
  } else {
    $(".canada-cholesterol-dv").fadeOut("fast", rebuildFunction);
  }
}

function bindPotassiumToggle() {
  //Show potassium div on button click
  $("#potassiumtoggle").on("click", (e) => {
    togglePotassium(e.target.checked);

    saveLabel({ show_potassium: e.target.checked });
  });
}

export function togglePotassium(display) {
  if (isLegacyFDA()) {
    toggleOverrideInput("potassium", display);
  }

  //logic for showing and hiding
  if (isFormat("legacy_fda/adult/vertical")) {
    if (display) {
      $("#potassium, #potassiumhairline, #potassiumpercentconversion").fadeIn("slow");
    } else {
      $("#potassium, #potassiumhairline, #potassiumpercentconversion").fadeOut("fast");
    }
  } else if (isFormat("legacy_fda/adult/linear")) {
    if (display) {
      $("#linearpotassium").fadeIn("slow");
    } else {
      $("#linearpotassium").fadeOut("slow");
    }
  } else if (isFormat("legacy_fda/adult/vertical/as_prepared")) {
    if (display) {
      $("#potassium-aggregate, #potassiumpercentconversionaggregate").fadeIn("slow");
    } else {
      $("#potassium-aggregate, #potassiumpercentconversionaggregate").fadeOut("slow");
    }
  } else if (isFormat("legacy_fda/adult/tabular")) {
    if (display) {
      $("#potassiumtabular, #potassiumpercentconversiontabular").fadeIn("slow", () => {
        tabularShuffle();
      });
    } else {
      $("#potassiumtabular, #potassiumpercentconversiontabular").fadeOut("slow", () => {
        tabularShuffle();
      });
    }
  }
}

function bindOtherCarbohydrateToggle() {
  //Show other carb on button click
  $("#othercarbtoggle").on("click", (e) => {
    toggleOtherCarbohyrate(e.target.checked);

    saveLabel({ show_other_carbs: e.target.checked });
  });
}

export function toggleOtherCarbohyrate(display) {
  // logic for both showing and hiding
  if (isFormat("legacy_fda/adult/vertical")) {
    if (display) {
      $("#othercarb").fadeIn("slow");
    } else {
      $("#othercarb").fadeOut("fast");
    }
  } else if (isFormat("legacy_fda/adult/linear")) {
    if (display) {
      $("#linearothercarb").fadeIn("slow");
    } else {
      $("#linearothercarb").fadeOut("slow");
    }
  } else if (isFormat("legacy_fda/adult/vertical/as_prepared")) {
    if (display) {
      $("#othercarb-aggregate").fadeIn("slow");
    } else {
      $("#othercarb-aggregate").fadeOut("slow");
    }
  } else if (isFormat("legacy_fda/adult/tabular")) {
    if (display) {
      $("#othercarbtabular").fadeIn("slow", () => {
        tabularShuffle();
      });
    } else {
      $("#othercarbtabular").fadeOut("slow", () => {
        tabularShuffle();
      });
    }
  }
}

export function toggleVariedServings(display) {
  if (display) {
    $(".servings-per-container").hide();
    $(".servings-per-container-varied").show(() => {
      if (isFormat("fda/adult/linear")) {
        adjustNewLinearLineHeightForCalorieFont();
      }
    });
  } else {
    $(".servings-per-container-varied").hide();
    $(".servings-per-container").show(() => {
      if (isFormat("fda/adult/linear")) {
        adjustNewLinearLineHeightForCalorieFont();
      }
    });
  }
}

function bindAddedSugar() {
  // Added Sugar grams toggle for single ingredient sugars (honey, syrup, etc.)
  $("#hideaddedsugartoggle").on("click", (e) => {
    toggleAddedSugar(!e.target.checked);

    showFootnoteSeparator();

    if (isFormat("fda/adult/tabular")) {
      refreshSlider("#newtabularslider");
    }

    saveLabel({ show_added_sugar_grams: !e.target.checked });
  });
}

export function toggleAddedSugar(display) {
  if (display) {
    $(".added-sugar-grams").show();
    $(".added-sugar-footnote").hide();
  } else {
    $(".added-sugar-grams").hide();
    $(".added-sugar-footnote").show();
  }
}

function bindProteinPecent() {
  $("#protein-percent-toggle").on("click", (e) => {
    toggleProteinPercent(e.target.checked);

    saveLabel({ show_protein_percent: e.target.checked });
  });

  $("#protein-score, #protein-score-required").on("change", (event) => {
    if (event.target.validity.valid) {
      event.target.classList.remove("is-danger");
      event.target._tippy?.destroy();
      saveProteinScore(event.target.value);
    } else {
      event.target.classList.add("is-danger");
      setTimeout(() => {
        displayError(event.target, event.target.validationMessage);
      }, 50);
    }
  });
}

function toggleProteinPercent(display) {
  if (display) {
    $(".protein-percent.optional, .protein-percent-conversion.optional").fadeIn("slow");
    document.querySelector("#protein-score-container").classList.remove("is-hidden");
  } else {
    $(".protein-percent.optional, .protein-percent-conversion.optional").fadeOut("fast");
    document.querySelector("#protein-score-container").classList.add("is-hidden");
  }
}

const wrapLinearNutrient = (content) => `<span class="linearnutrientcontent">${content}</span>`;

function updateInsignificantFootnoteContent({ text, shown, simplified }) {
  if (isFormat("fda/adult/vertical")) {
    const footnote = document.querySelector("#new-vertical-insignificant-nutrients-list");
    if (!footnote) return;

    footnote.textContent = text;
    if (!simplified) toggleInsignificant(shown, simplified);
    showFootnoteSeparator();
  } else if (isFormat("fda/adult/linear")) {
    const footnote = document.querySelector("#insignificant-nutrients-list-new-linear");
    if (!footnote) return;

    let wrappedText = "";
    if (text) {
      text.split(" ").forEach((word) => {
        if (word !== "Not") wrappedText += wrapLinearNutrient("&#8197;");
        wrappedText += wrapLinearNutrient(word);
      });
      wrappedText += wrapLinearNutrient(".&#8197;");
    }
    footnote.innerHTML = wrappedText;
    if (!simplified) toggleInsignificant(shown, simplified);
  } else if (isFormat("fda/adult/tabular")) {
    // The footnote wraps its text in a span so the sibling hairlineseparator
    // (toggled by vitamin visibility) survives content updates.
    const footnote = document.querySelector(
      "#insignificant-nutrients-list-new-tabular .insignificant-nutrients-text"
    );
    if (!footnote) return;

    footnote.textContent = text;
    if (!simplified) {
      toggleInsignificant(shown, simplified);
    } else {
      syncTabularHairline();
    }
  } else if (isFormat("fda/adult/vertical/as_prepared")) {
    const footnote = document.querySelector("#insignificant-nutrients-list-new-as-packaged");
    if (!footnote) return;

    footnote.textContent = text;
    if (!simplified) toggleInsignificant(shown, simplified);
    showFootnoteSeparator();
  } else if (isFormat("fda/adult/vertical/aggregate")) {
    const footnote = document.querySelector("#insignificant-nutrients-list-new-aggregate");
    if (!footnote) return;

    footnote.textContent = text;
    if (!simplified) toggleInsignificant(shown, simplified);
  }
}

function bindNutrientVisibilityToggles() {
  $("input.nutrientvisibilitytoggle").on("change", (e) => {
    toggleNutrientVisibility(e.target.dataset.nutrient, e.target.checked);
    saveLabel({ nutrient_visibility: currentNutrientVisibility() });
  });

  once(document, "insignificant-footnote", () => {
    document.addEventListener("label:insignificant-footnote", (e) =>
      applyInsignificantFootnote(e.detail)
    );
  });
}

function toggleNutrientVisibility(nutrient, checked) {
  const format = currentFormat();
  const selector = SELECTOR_FOR_NUTRIENT_WITH_VISIBILITY_TOGGLE[format][nutrient];
  if (selector) {
    const selectors = [selector];
    const event = showsOnLabel(checked) ? "##checked" : "##unchecked";
    const dependentNutrients = DEPENDENT_NUTRIENTS[nutrient]?.flatMap((dep) => dep[event] ?? []);
    if (dependentNutrients?.length) {
      const dependentCheckboxSelectors = dependentNutrients.map(
        (dep) => `#${dep}-visibility-toggle`
      );
      $(dependentCheckboxSelectors.join(", ")).prop("checked", checked);
      selectors.push(
        ...dependentNutrients.map(
          (dep) => SELECTOR_FOR_NUTRIENT_WITH_VISIBILITY_TOGGLE[format][dep]
        )
      );
    }

    $(selectors.join(", ")).toggle(showsOnLabel(checked));
    const callback = NUTRIENT_TOGGLE_CALLBACK[format];
    if (callback) callback();
  } else {
    rebuildFunction();
  }
}

function showsOnLabel(checked) {
  return inSimplifiedMode() ? checked : !checked;
}

function applyInsignificantFootnote(event) {
  const { simplified } = event;
  if (!formatSupportsVisibilityToggle()) return;

  // saveLabel responses can arrive out of order
  if (inSimplifiedMode() !== simplified) return;

  updateInsignificantFootnoteContent(event);
}

function currentNutrientVisibility() {
  const desired = inSimplifiedMode() ? "show" : "hide";
  const visibility = {};
  Object.entries(window.recipe_label.nutrient_visibility || {}).forEach(([key, value]) => {
    if (value !== desired) visibility[key] = value;
  });
  $("input.nutrientvisibilitytoggle:checked").each((_index, el) => {
    visibility[el.dataset.nutrient] = desired;
  });
  return visibility;
}

function syncVisibilityToggleCheckboxes(label, simplified) {
  const visibility = label.nutrient_visibility || {};

  $(".hide-nutrient-label").toggleClass("is-hidden", simplified);
  $(".show-nutrient-label").toggleClass("is-hidden", !simplified);

  let anyChecked = false;
  $("input.nutrientvisibilitytoggle").each((_index, el) => {
    const nutrient = el.dataset.nutrient;
    const state = visibility[nutrient];
    el.checked = simplified ? state === "show" : state === "hide";
    anyChecked = anyChecked || el.checked;
  });

  return anyChecked;
}

function syncInsignificantVisibility(simplified, anyChecked) {
  rebuildFunction();
  if (simplified) {
    const insignificant = insignificantCheckboxForFormat();
    if (!insignificant || insignificant.length === 0) return;

    toggleInsignificant(insignificant.is(":checked"), simplified);
  } else {
    // When we replace:partial on the label, we call toggleInsignificant with
    // the checkbox state. In standard labels if we don't have anything checked
    // we need to hide the insignificant text
    toggleInsignificant(anyChecked, simplified);
  }
}
